/**
 * Wave 4 — earnest deposit tracker (synced from contract offer fields).
 */

import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { toast } from 'sonner';

import {
  useGetClosingOrchestrationQuery,
  useUpdateEarnestDepositMutation,
  type EarnestDeposit,
} from '@/apis/extended/closingApi';

const statusColor: Record<
  EarnestDeposit['status'],
  'default' | 'warning' | 'success'
> = {
  pending: 'warning',
  submitted: 'default',
  confirmed: 'success',
};

export function EarnestMoneyTracker({
  contractId,
  readOnly = false,
}: {
  contractId: string;
  readOnly?: boolean;
}) {
  const { data: closing, isLoading } = useGetClosingOrchestrationQuery(
    contractId,
    {
      skip: !contractId,
    }
  );
  const [updateEarnest, { isLoading: updating }] =
    useUpdateEarnestDepositMutation();

  const deposits = closing?.earnestDeposits ?? [];

  const handleStatus = async (
    key: string,
    status: EarnestDeposit['status']
  ) => {
    try {
      await updateEarnest({ contractId, key, status }).unwrap();
      toast.success('Earnest status updated');
    } catch {
      toast.error('Could not update earnest status');
    }
  };

  if (isLoading) {
    return <Typography variant="body2">Loading earnest deposits…</Typography>;
  }

  if (deposits.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No earnest deposits configured on this contract yet.
      </Typography>
    );
  }

  return (
    <Stack gap={1.5} className="taste-rise">
      {deposits.map((d) => (
        <Box
          key={d.key}
          className="taste-panel"
          sx={{
            p: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {d.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              $
              {Number(d.amount || 0).toLocaleString('en-US', {
                maximumFractionDigits: 0,
              })}
              {d.dueDate ? ` · due ${d.dueDate}` : ''}
            </Typography>
          </Box>
          <Chip size="small" color={statusColor[d.status]} label={d.status} />
          {!readOnly && d.status !== 'confirmed' && (
            <Stack direction="row" spacing={1}>
              {d.status === 'pending' && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={updating}
                  onClick={() => handleStatus(d.key, 'submitted')}
                >
                  Mark submitted
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                disabled={updating}
                onClick={() => handleStatus(d.key, 'confirmed')}
              >
                Confirm received
              </Button>
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  );
}
